import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Modal from "@/components/base/Modal";
import { Field, Input, Select, Textarea } from "@/components/base/FormControls";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import {
  AGENCE_BIEN_TYPE_LABELS,
  AGENCE_CONTRAT_STATUT_OPTIONS,
  agenceLocataireNomComplet,
  type AgenceContrat,
  type AgenceContratInput,
  type AgenceStatutContrat,
} from "@/pages/agence/data/types";
import { useProprietaires } from "@/pages/agence/proprietaires/ProprietairesProvider";
import { proprietaireNomComplet } from "@/pages/agence/proprietaires/types";

interface ContratFormProps {
  open: boolean;
  onClose: () => void;
  defaultProprietaireId?: string;
  defaultImmeubleId?: string;
  defaultBienId?: string;
  defaultLocataireId?: string;
  initial?: AgenceContrat;
}

interface FormState {
  proprietaireId: string;
  immeubleId: string;
  bienId: string;
  locataireId: string;
  dateDebut: string;
  dateFin: string;
  loyerMensuel: string;
  caution: string;
  avance: string;
  jourEcheance: string;
  conditionsParticulieres: string;
  document: string;
  statut: AgenceStatutContrat;
}

const EMPTY_STATE: FormState = {
  proprietaireId: "",
  immeubleId: "",
  bienId: "",
  locataireId: "",
  dateDebut: "",
  dateFin: "",
  loyerMensuel: "",
  caution: "",
  avance: "",
  jourEcheance: "",
  conditionsParticulieres: "",
  document: "",
  statut: "brouillon",
};

export default function ContratForm({
  open,
  onClose,
  defaultProprietaireId,
  defaultImmeubleId,
  defaultBienId,
  defaultLocataireId,
  initial,
}: ContratFormProps) {
  const { immeubles, biens, locataires, addContrat, updateContrat, getBien } = useAgenceData();
  const { proprietaires } = useProprietaires();
  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        proprietaireId: initial.proprietaireId,
        immeubleId: initial.immeubleId,
        bienId: initial.bienId,
        locataireId: initial.locataireId,
        dateDebut: initial.dateDebut,
        dateFin: initial.dateFin,
        loyerMensuel: String(initial.loyerMensuel),
        caution: initial.caution ? String(initial.caution) : "",
        avance: initial.avance ? String(initial.avance) : "",
        jourEcheance: initial.jourEcheance ? String(initial.jourEcheance) : "",
        conditionsParticulieres: initial.conditionsParticulieres,
        document: initial.document,
        statut: initial.statut,
      });
      setErrors({});
      return;
    }
    setForm((prev) => {
      const next = { ...prev };
      if (defaultImmeubleId) {
        next.immeubleId = defaultImmeubleId;
        const immeuble = immeubles.find((item) => item.id === defaultImmeubleId);
        if (immeuble) next.proprietaireId = immeuble.proprietaireId;
      }
      if (defaultBienId) {
        next.bienId = defaultBienId;
        const bien = getBien(defaultBienId);
        if (bien) {
          next.immeubleId = bien.immeubleId;
          next.proprietaireId = bien.proprietaireId;
          if (!prev.loyerMensuel) next.loyerMensuel = String(bien.loyerMensuel);
        }
      }
      if (defaultProprietaireId) next.proprietaireId = defaultProprietaireId;
      if (defaultLocataireId) next.locataireId = defaultLocataireId;
      return next;
    });
  }, [open, defaultProprietaireId, defaultImmeubleId, defaultBienId, defaultLocataireId, immeubles, getBien]);

  const proprietaireOptions = useMemo(
    () => proprietaires.map((item) => ({ value: item.id, label: proprietaireNomComplet(item) })),
    [proprietaires]
  );

  const locataireOptions = useMemo(
    () => locataires.map((item) => ({ value: item.id, label: agenceLocataireNomComplet(item) })),
    [locataires]
  );

  const immeubleOptions = useMemo(
    () =>
      immeubles
        .filter((immeuble) => !form.proprietaireId || immeuble.proprietaireId === form.proprietaireId)
        .map((immeuble) => ({ value: immeuble.id, label: immeuble.nom })),
    [immeubles, form.proprietaireId]
  );

  const bienOptions = useMemo(
    () =>
      biens
        .filter((bien) => !form.immeubleId || bien.immeubleId === form.immeubleId)
        .map((bien) => ({
          value: bien.id,
          label: `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` · ${bien.reference}` : ""}`,
        })),
    [biens, form.immeubleId]
  );

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleProprietaire = (value: string) => {
    setForm((prev) => ({ ...prev, proprietaireId: value, immeubleId: "", bienId: "" }));
  };

  const handleImmeuble = (value: string) => {
    const immeuble = immeubles.find((item) => item.id === value);
    setForm((prev) => ({
      ...prev,
      immeubleId: value,
      bienId: "",
      proprietaireId: immeuble ? immeuble.proprietaireId : prev.proprietaireId,
    }));
  };

  const handleBien = (value: string) => {
    const bien = getBien(value);
    setForm((prev) => ({
      ...prev,
      bienId: value,
      immeubleId: bien ? bien.immeubleId : prev.immeubleId,
      proprietaireId: bien ? bien.proprietaireId : prev.proprietaireId,
      loyerMensuel: bien && !prev.loyerMensuel ? String(bien.loyerMensuel) : prev.loyerMensuel,
    }));
  };

  const handleDocument = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    update("document", file ? file.name : "");
  };

  const resetAndClose = () => {
    setForm(EMPTY_STATE);
    setErrors({});
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: Record<string, string> = {};
    if (!form.proprietaireId) nextErrors.proprietaireId = "Sélectionnez le propriétaire.";
    if (!form.immeubleId) nextErrors.immeubleId = "Sélectionnez l'immeuble du contrat.";
    if (!form.bienId) nextErrors.bienId = "Sélectionnez le bien concerné.";
    if (!form.locataireId) nextErrors.locataireId = "Sélectionnez le locataire.";
    if (!form.dateDebut) nextErrors.dateDebut = "Indiquez la date de début.";
    if (!form.loyerMensuel.trim() || Number(form.loyerMensuel) <= 0) {
      nextErrors.loyerMensuel = "Indiquez un loyer mensuel valide.";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const input: AgenceContratInput = {
      proprietaireId: form.proprietaireId,
      immeubleId: form.immeubleId,
      bienId: form.bienId,
      locataireId: form.locataireId,
      dateDebut: form.dateDebut,
      dateFin: form.dateFin,
      loyerMensuel: Math.max(0, Number(form.loyerMensuel) || 0),
      caution: Math.max(0, Number(form.caution) || 0),
      avance: Math.max(0, Number(form.avance) || 0),
      jourEcheance: Math.min(31, Math.max(1, Math.round(Number(form.jourEcheance) || 1))),
      conditionsParticulieres: form.conditionsParticulieres.trim(),
      document: form.document,
      statut: form.statut,
    };

    if (initial) {
      updateContrat(initial.id, input);
    } else {
      addContrat(input);
    }
    resetAndClose();
  };

  const noProprietaire = proprietaires.length === 0;
  const noImmeuble = immeubles.length === 0;
  const noBien = biens.length === 0;
  const noLocataire = locataires.length === 0;
  const canSubmit = !noProprietaire && !noImmeuble && !noBien && !noLocataire;

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      size="lg"
      title={initial ? "Modifier le contrat" : "Ajouter un contrat"}
      description="Le contrat respecte les relations existantes : propriétaire → immeuble → bien, puis le locataire."
      footer={
        <>
          <button
            type="button"
            onClick={resetAndClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="agence-contrat-form"
            disabled={!canSubmit}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
          >
            <i className="ri-check-line text-base" aria-hidden="true" />
            Enregistrer le contrat
          </button>
        </>
      }
    >
      <form id="agence-contrat-form" onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
          <Field label="Propriétaire" htmlFor="agence-ctr-proprietaire" required error={errors.proprietaireId}>
            <Select
              id="agence-ctr-proprietaire"
              value={form.proprietaireId}
              onChange={(event) => handleProprietaire(event.target.value)}
              options={proprietaireOptions}
              placeholder={proprietaireOptions.length > 0 ? "Sélectionnez un propriétaire" : "Aucun propriétaire"}
              disabled={noProprietaire}
              invalid={Boolean(errors.proprietaireId)}
            />
          </Field>

          <Field label="Immeuble" htmlFor="agence-ctr-immeuble" required error={errors.immeubleId}>
            <Select
              id="agence-ctr-immeuble"
              value={form.immeubleId}
              onChange={(event) => handleImmeuble(event.target.value)}
              options={immeubleOptions}
              placeholder={
                immeubleOptions.length > 0 ? "Sélectionnez un immeuble" : "Aucun immeuble disponible"
              }
              disabled={immeubleOptions.length === 0}
              invalid={Boolean(errors.immeubleId)}
            />
          </Field>

          <Field
            label="Bien"
            htmlFor="agence-ctr-bien"
            required
            error={errors.bienId}
            hint="Les biens affichés correspondent à l'immeuble sélectionné."
          >
            <Select
              id="agence-ctr-bien"
              value={form.bienId}
              onChange={(event) => handleBien(event.target.value)}
              options={bienOptions}
              placeholder={
                bienOptions.length > 0 ? "Sélectionnez un bien" : "Aucun bien disponible"
              }
              disabled={bienOptions.length === 0}
              invalid={Boolean(errors.bienId)}
            />
          </Field>

          <Field
            label="Locataire"
            htmlFor="agence-ctr-locataire"
            required
            error={errors.locataireId}
            hint="Choisissez un locataire déjà créé dans le module Locataires."
          >
            <Select
              id="agence-ctr-locataire"
              value={form.locataireId}
              onChange={(event) => update("locataireId", event.target.value)}
              options={locataireOptions}
              placeholder={locataireOptions.length > 0 ? "Sélectionnez un locataire" : "Aucun locataire"}
              disabled={noLocataire}
              invalid={Boolean(errors.locataireId)}
            />
          </Field>

          <Field label="Date de début" htmlFor="agence-ctr-debut" required error={errors.dateDebut}>
            <Input
              id="agence-ctr-debut"
              type="date"
              value={form.dateDebut}
              onChange={(event) => update("dateDebut", event.target.value)}
              invalid={Boolean(errors.dateDebut)}
            />
          </Field>

          <Field label="Date de fin" htmlFor="agence-ctr-fin">
            <Input
              id="agence-ctr-fin"
              type="date"
              value={form.dateFin}
              onChange={(event) => update("dateFin", event.target.value)}
            />
          </Field>

          <Field label="Loyer mensuel (FCFA)" htmlFor="agence-ctr-loyer" required error={errors.loyerMensuel}>
            <Input
              id="agence-ctr-loyer"
              type="number"
              min={0}
              max={100000000}
              value={form.loyerMensuel}
              onChange={(event) => update("loyerMensuel", event.target.value)}
              placeholder="Ex. 75000"
              invalid={Boolean(errors.loyerMensuel)}
            />
          </Field>

          <Field label="Jour d'échéance" htmlFor="agence-ctr-echeance" hint="Jour du mois (1 à 31).">
            <Input
              id="agence-ctr-echeance"
              type="number"
              min={1}
              max={31}
              step={1}
              value={form.jourEcheance}
              onChange={(event) => update("jourEcheance", event.target.value)}
              placeholder="Ex. 5"
            />
          </Field>

          <Field label="Caution (FCFA)" htmlFor="agence-ctr-caution">
            <Input
              id="agence-ctr-caution"
              type="number"
              min={0}
              max={100000000}
              value={form.caution}
              onChange={(event) => update("caution", event.target.value)}
              placeholder="Ex. 150000"
            />
          </Field>

          <Field label="Avance (FCFA)" htmlFor="agence-ctr-avance">
            <Input
              id="agence-ctr-avance"
              type="number"
              min={0}
              max={100000000}
              value={form.avance}
              onChange={(event) => update("avance", event.target.value)}
              placeholder="Ex. 75000"
            />
          </Field>

          <Field label="Statut" htmlFor="agence-ctr-statut">
            <Select
              id="agence-ctr-statut"
              value={form.statut}
              onChange={(event) => update("statut", event.target.value)}
              options={AGENCE_CONTRAT_STATUT_OPTIONS}
            />
          </Field>

          <Field label="Document du contrat" htmlFor="agence-ctr-document" hint="PDF ou image (le fichier reste local).">
            <Input
              id="agence-ctr-document"
              type="file"
              accept="application/pdf,image/*"
              onChange={handleDocument}
              className="file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-background-100 file:px-3 file:py-1.5 file:text-sm file:font-label file:text-foreground-800"
            />
          </Field>

          <Field label="Conditions particulières" htmlFor="agence-ctr-conditions" className="md:col-span-2">
            <Textarea
              id="agence-ctr-conditions"
              value={form.conditionsParticulieres}
              onChange={(event) => update("conditionsParticulieres", event.target.value)}
              placeholder="Clauses spécifiques, charges, travaux, modalités de paiement..."
              className="min-h-[96px]"
              maxLength={600}
            />
          </Field>
        </div>

        {!canSubmit && (
          <p className="flex flex-wrap items-center gap-2 rounded-md bg-background-100 px-3.5 py-3 text-xs text-foreground-600">
            <i className="ri-information-line text-base text-foreground-400" aria-hidden="true" />
            Un contrat nécessite un propriétaire, un immeuble, un bien et un locataire.
            <Link to="/agence/immeubles" className="font-label font-medium text-primary-700 cursor-pointer">
              Aller aux immeubles
            </Link>
            <Link to="/agence/locataires" className="font-label font-medium text-primary-700 cursor-pointer">
              Aller aux locataires
            </Link>
          </p>
        )}
      </form>
    </Modal>
  );
}